import React from "react";
import {
  caretIndexAfterDigits,
  countAmountDigits,
  formatAmountInput,
  sanitizeAmountInput,
} from "../../utils/amountFormat";

/**
 * Text input for whole-number amounts (IQD).
 *
 * - Only digits can be entered; letters, symbols, decimals, spaces etc. are
 *   dropped whether typed or pasted.
 * - Thousand separators are shown live while typing (1000 -> 1,000).
 * - `value` may be a number or string (including API decimals like
 *   "20000.00"); `onValueChange` always receives the plain digit string
 *   ("12000"), so parent state and API payloads never contain commas.
 *
 * Any other props (placeholder, className, style, disabled, autoFocus, ...)
 * are forwarded to the underlying <input>.
 */
const AmountInput = ({ value, onValueChange, onKeyDown, ...rest }) => {
  const handleChange = (e) => {
    const input = e.target;
    const raw = input.value;
    const caret = input.selectionStart ?? raw.length;

    const next = sanitizeAmountInput(raw);
    // Leading zeros are dropped, so they no longer count towards the caret.
    const leadingZerosDropped = countAmountDigits(raw) - next.length;
    const digitsBeforeCaret = Math.max(
      0,
      countAmountDigits(raw.slice(0, caret)) - leadingZerosDropped,
    );

    onValueChange?.(next);

    // Re-formatting (or React restoring the controlled value after an invalid
    // character) moves the caret to the end. Once the new value is committed,
    // put the caret back right after the same digit.
    requestAnimationFrame(() => {
      if (document.activeElement !== input) return;
      const pos = caretIndexAfterDigits(input.value, digitsBeforeCaret);
      input.setSelectionRange(pos, pos);
    });
  };

  // Backspace/Delete right next to a separator removes the neighbouring digit
  // instead of the comma (which would just be re-added by the formatter).
  const handleKeyDown = (e) => {
    const input = e.currentTarget;
    const { selectionStart: start, selectionEnd: end, value: shown } = input;
    if (start !== null && start === end) {
      if (e.key === "Backspace" && start > 0 && shown[start - 1] === ",") {
        input.setSelectionRange(start - 1, start - 1);
      } else if (e.key === "Delete" && shown[start] === ",") {
        input.setSelectionRange(start + 1, start + 1);
      }
    }
    onKeyDown?.(e);
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      autoComplete="off"
      {...rest}
      value={formatAmountInput(value)}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
    />
  );
};

export default AmountInput;
